import {
  FieldInputValue,
  InputFieldProps,
  mapSkillsToAutocompleteValues,
  SkillOption
} from 'components/Forms';
import {
  AdjustmentType,
  DayOfWeek,
  ScheduleAdjustmentReason
} from 'models/Adjustment';
import AdjustmentGroup from 'models/AdjustmentGroup';
import Skill from 'models/Skills';
import { getAdjustmentChange, getAdjustmentForDay } from 'utils/adjustments';
import * as Yup from 'yup';
import {
  adjustmentCommonFieldsInitialValues,
  adjustmentCommonFieldsValidationSchema,
  adjustmentWeekdayKeys,
  getAdjustmentCommonFields
} from './adjustmentFormCommonConfig';

export const getDayFieldName = (day: string) => `skillsChange${day}`;

const getSkillsFieldForWeekday = (day: string): InputFieldProps => {
  const fieldName = getDayFieldName(day);
  return {
    id: fieldName,
    required: true,
    name: fieldName,
    qaId: fieldName,
    inputType: 'multiselect',
    optionRenderer: SkillOption,
    options: [],
    preprocess: mapSkillsToAutocompleteValues
  };
};

const skillsReasonOptions = (): FieldInputValue[] => {
  return [
    {
      value: ScheduleAdjustmentReason.VoidsRC,
      label: ScheduleAdjustmentReason.VoidsRC,
      key: 'VoidsRC'
    },
    {
      value: ScheduleAdjustmentReason.ComplaintsRC,
      label: ScheduleAdjustmentReason.ComplaintsRC,
      key: 'ComplaintsRC'
    },
    {
      value: ScheduleAdjustmentReason.ReservedCapacity,
      label: ScheduleAdjustmentReason.ReservedCapacity,
      key: 'ReservedCapacity'
    },
    {
      value: ScheduleAdjustmentReason.Planning,
      label: ScheduleAdjustmentReason.Planning,
      key: 'Planning'
    },
    {
      value: ScheduleAdjustmentReason.EV,
      label: ScheduleAdjustmentReason.EV,
      key: 'EV'
    },
    {
      value: ScheduleAdjustmentReason.Overtime,
      label: ScheduleAdjustmentReason.Overtime,
      key: 'Overtime'
    },
    {
      value: ScheduleAdjustmentReason.Competency,
      label: ScheduleAdjustmentReason.Competency,
      key: 'Competency'
    },
    {
      value: ScheduleAdjustmentReason.CoverageRotation,
      label: ScheduleAdjustmentReason.CoverageRotation,
      key: 'CoverageRotation'
    }
  ];
};

export const skillsFormCommonFields = getAdjustmentCommonFields(
  skillsReasonOptions()
);

export const skillsFormWeekdayFields = Object.fromEntries(
  adjustmentWeekdayKeys.map(day => [day, getSkillsFieldForWeekday(day)])
);

export const skillsFormValidationSchema = Yup.object().shape(
  adjustmentCommonFieldsValidationSchema
);

const getWeekdaySkillsFromGroup = (
  day: DayOfWeek,
  adjustmentGroup: AdjustmentGroup
) => {
  const adjustment = getAdjustmentForDay(day, adjustmentGroup.adjustments);

  return adjustment
    ? getAdjustmentChange(adjustment, AdjustmentType.Skills)?.change
    : undefined;
};

export const skillsFormWeekdaysInitialValues = (
  adjustmentGroup: AdjustmentGroup | undefined
) => {
  const fieldNameSkillsPairs = adjustmentWeekdayKeys.map(day => {
    // TODO Fix weekday types: FTSC-1391
    const adjustmentGroupSkills = adjustmentGroup
      ? getWeekdaySkillsFromGroup(
          day.toUpperCase() as DayOfWeek,
          adjustmentGroup
        )
      : null;

    const adjustmentGroupSkillsToAutocomplete = (adjustmentGroupSkills as Skill[])?.map(
      skill => ({
        label: skill.name,
        value: skill.id,
        category: skill.category
      })
    );
    return [getDayFieldName(day), adjustmentGroupSkillsToAutocomplete || []];
  });

  return Object.fromEntries(fieldNameSkillsPairs);
};

export const skillsFormInitialValues = (adjustmentGroup?: AdjustmentGroup) => {
  const skillsDaysInitialValues = skillsFormWeekdaysInitialValues(
    adjustmentGroup
  );

  return {
    ...adjustmentCommonFieldsInitialValues(adjustmentGroup?.adjustments[0]),
    ...skillsDaysInitialValues
  };
};
